import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface DocCategory {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  icon: string | null;
  display_order: number | null;
  is_active: boolean | null;
  doc_type: string | null;
}

interface Documentation {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string | null;
  category_id: string;
  display_order: number | null;
  is_active: boolean | null;
  created_at: string;
  updated_at: string;
}

interface DocumentationWithCategory extends Documentation {
  category: DocCategory;
}

export function useGuideCategories() {
  return useQuery({
    queryKey: ['doc-categories', 'guide'],
    staleTime: 5 * 60 * 1000, // 5 minutes - documentation categories
    queryFn: async () => {
      const { data, error } = await supabase
        .from('doc_categories')
        .select('id, name, slug, description, icon, display_order, is_active, doc_type')
        .eq('is_active', true)
        .order('display_order', { ascending: true });
      
      if (error) throw error;
      
      // Filter for guide type (default or explicit)
      return (data as DocCategory[]).filter(
        (cat) => !cat.doc_type || cat.doc_type === 'guide'
      );
    },
  });
}

export function useGuides() {
  return useQuery({
    queryKey: ['documentation', 'guides'],
    staleTime: 5 * 60 * 1000, // 5 minutes - documentation list
    queryFn: async () => {
      // First get guide categories
      const { data: categories, error: catError } = await supabase
        .from('doc_categories')
        .select('id, name, slug, description, icon, display_order, is_active, doc_type')
        .eq('is_active', true)
        .order('display_order', { ascending: true });
      
      if (catError) throw catError;
      
      const guideCategories = (categories as DocCategory[]).filter(
        (cat) => !cat.doc_type || cat.doc_type === 'guide'
      );
      
      const categoryIds = guideCategories.map((c) => c.id);
      
      // Then get docs for those categories
      const { data: docs, error: docError } = await supabase
        .from('documentation')
        .select('id, title, slug, content, excerpt, category_id, display_order, is_active, created_at, updated_at')
        .eq('is_active', true)
        .in('category_id', categoryIds)
        .order('display_order', { ascending: true });
      
      if (docError) throw docError;
      
      // Combine with category info
      const docsWithCategory: DocumentationWithCategory[] = (docs || []).map((doc) => ({
        ...doc,
        category: guideCategories.find((c) => c.id === doc.category_id)!,
      }));
      
      return {
        categories: guideCategories,
        docs: docsWithCategory,
      };
    },
  });
}

export function useGuide(slug: string) {
  return useQuery({
    queryKey: ['documentation', 'guide', slug],
    staleTime: 5 * 60 * 1000, // 5 minutes - individual guide
    queryFn: async () => {
      const { data, error } = await supabase
        .from('documentation')
        .select(`
          id, title, slug, content, excerpt, category_id, display_order, is_active, created_at, updated_at,
          doc_categories!category_id (
            id, name, slug, description, icon
          )
        `)
        .eq('slug', slug)
        .eq('is_active', true)
        .single();
      
      if (error) throw error;
      
      return data as Documentation & { doc_categories: DocCategory };
    },
    enabled: !!slug,
  });
}
